import { Link } from "react-router-dom";
import { ArrowRight, FlaskConical, Globe2, ShieldCheck, Leaf } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Tag } from "@/components/Reveal";
import { ParallaxImage } from "@/components/ParallaxImage";
import { IMG } from "@/data/images";

const VALUES = [
  { icon: FlaskConical, title: "Science before claims", text: "We publish targets, not miracles. Every performance figure is tied to trials and validation." },
  { icon: Leaf, title: "Sustainability by design", text: "If a solution creates new waste while saving food, it isn't a solution. Ours is plant-derived and residue-conscious." },
  { icon: ShieldCheck, title: "Credibility always", text: "Investors, partners and regulators get the same honest picture — progress, gaps and all." },
  { icon: Globe2, title: "Global ambition", text: "Founded in Riyadh, engineered for every market where heat and distance steal freshness." },
];

export default function OurStory() {
  return (
    <div data-testid="our-story-page">
      <PageHero
        chapter="01"
        tag="Our Story"
        testId="story-hero"
        lines={[<>Born in Riyadh.</>, <>Built for the world&rsquo;s</>, <>fresh future.</>]}
        lead="Fresh Guard began with a simple observation: in a region that imports most of its food, freshness is not a luxury — it is security."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div className="space-y-6 text-base sm:text-lg leading-relaxed text-moss">
          <Reveal>
            <p>
              Every year, vast quantities of fruit and vegetables travel thousands of kilometres
              to reach the Kingdom — only to lose their freshness in the final stretch. For
              consumers that means disappointment. For businesses, margin. For the planet,
              squandered water, land and energy.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p>
              Our founding team of food scientists and supply-chain operators asked a different
              question: what if produce could protect itself? Not with synthetic preservatives,
              but with plant-derived science that works with the biology of fresh food —
              slowing respiration, holding moisture, and defending against spoilage microbes.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p>
              That question became Fresh Guard: a technology company at the intersection of food
              science, agricultural research, smart packaging and supply-chain intelligence,
              aligned with Saudi Vision 2030&rsquo;s goals for food security and sustainability.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/technology"
              data-testid="story-cta-technology"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-emerald hover:text-forest transition-colors pt-2"
            >
              Discover the science
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Reveal delay={0.1}>
            <ParallaxImage src={IMG.lettuceField} alt="Rows of fresh lettuce growing in a field" ratio="aspect-[3/4]" speed={9} testId="story-image-field" />
          </Reveal>
          <Reveal delay={0.25} className="mt-10">
            <ParallaxImage src={IMG.labMicroscope} alt="Scientist examining samples in a research laboratory" ratio="aspect-[3/4]" speed={12} testId="story-image-lab" />
          </Reveal>
        </div>
      </section>

      {/* FOUNDER & CEO */}
      <section className="bg-deep text-cream" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <Tag light>Founder & CEO</Tag>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="relative mt-10">
                <img
                  src="/founder-hassan-al-ahdal.jpg"
                  alt="Hassan Al-Ahdal, Founder and CEO of Fresh Guard"
                  className="w-full aspect-[3/4] object-cover object-top"
                  data-testid="founder-portrait"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-deep/95 via-deep/40 to-transparent px-6 py-7">
                  <p className="font-display font-bold text-2xl">Hassan Al-Ahdal</p>
                  <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-mint">
                    Founder & CEO — Fresh Guard
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pt-16" data-testid="founder-message">
            <Reveal>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">The Founder Message</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 font-serif italic text-2xl sm:text-3xl lg:text-[2.4rem] leading-snug text-cream">
                &ldquo;A better future for fresh food starts with giving it more time.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-8 space-y-5 text-base sm:text-lg leading-relaxed text-cream/80">
                <p>
                  Fresh Guard was born from a simple question: What if we could preserve more of
                  the value already created before food ever reaches the kitchen?
                </p>
                <p>
                  Every fruit and vegetable carries the cost of water, land, energy, labor, and
                  transportation. When it spoils too early, we lose far more than food.
                </p>
                <p>
                  We are building Fresh Guard to change that equation — through food science,
                  agricultural research, smart packaging, and intelligent supply chains.
                </p>
                <p>
                  We are starting in Saudi Arabia, where climate, distance, and food security
                  make this challenge especially meaningful. But our ambition goes beyond one
                  market.
                </p>
                <p>
                  We want to build the technology that gives fresh food more usable time — and
                  turns that time into less waste, greater resilience, and a more sustainable
                  food system.&rdquo;
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 flex items-center gap-5" data-testid="founder-signature">
                <span className="h-px w-12 bg-amber" />
                <div>
                  <p className="font-display font-bold text-xl text-cream">Hassan Al-Ahdal</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-sage">
                    Founder & CEO, Fresh Guard
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-forest/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 grid grid-cols-1 md:grid-cols-2 gap-8">
          <Reveal>
            <div className="border border-forest/10 bg-cream p-10 h-full" data-testid="vision-card">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber">Vision</p>
              <p className="mt-5 font-display font-bold text-2xl sm:text-3xl leading-snug text-forest">
                A world where fresh food&rsquo;s journey never ends in a bin.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-forest/10 bg-cream p-10 h-full" data-testid="mission-card">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber">Mission</p>
              <p className="mt-5 font-display font-bold text-2xl sm:text-3xl leading-snug text-forest">
                Engineer natural shelf-life extension that businesses trust and consumers never notice.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
        <Reveal><Tag>What we stand for</Tag></Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div
                data-testid={`value-card-${i + 1}`}
                className="group border border-forest/10 bg-white p-8 h-full transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-forest/10"
              >
                <v.icon className="h-6 w-6 text-emerald" />
                <h3 className="mt-5 font-display font-bold text-xl text-forest">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-moss">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
